import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getSources, saveSource } from "../../components/Api/GmsSource";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import classnames from "classnames";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const defaultForm = {
  Source_Id: 0,
  SourceName: "",
  SourceDetails: "",
  CreatedBy: 1,
  CreatedOn: new Date().toISOString(),
  ModifiedBy: null,
  ModifiedOn: null,
};

export default function SourcePage() {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSource');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSource');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSource');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadSources();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  useEffect(() => {
    if (page > 1) {
      loadSources();
    }
  }, [page]);

  const loadSources = async () => {
    setLoading(true);
    try {
      const filter = {
        Page: page,
        PageSize: pageSize,
      };
      const response = await getSources(filter);
      
      // Handle both camelCase and PascalCase response formats
      const sources = response?.Sources || response?.sources || [];
      const totalCount = response?.TotalCount || response?.totalCount || sources.length;
      
      setSources(sources);
      setTotalCount(totalCount);
    } catch (error) {
      console.error("Error loading sources:", error);
      Swal.fire("Error", "Failed to load sources: " + (error.response?.data?.message || error.message), "error");
      setSources([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.SourceName || !form.SourceName.trim()) {
      Swal.fire("Error", "Please enter source name", "error");
      return;
    }
    
    setSaveLoading(true);
    try {
      const formData = {
        ...form,
        CreatedBy: session.userId,
        ModifiedBy: isEditing ? session.userId : null,
      };
      
      const result = await saveSource(formData);
      
      if (result.id && result.id > 0) {
        Swal.fire("Success", result.message || "Source saved successfully", "success");
        // Reset form
        setForm(defaultForm);
        setIsEditing(false);
        // Reload sources
        loadSources();
        // Switch to list tab
        setActiveTab("2");
      } else {
        Swal.fire("Error", result.message || "Failed to save source", "error");
      }
    } catch (error) {
      console.error("Error saving source:", error);
      Swal.fire("Error", "Failed to save source: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (source) => {
    // Handle both camelCase and PascalCase from API
    setForm({
      Source_Id: source.Source_Id || source.source_Id || 0,
      SourceName: source.SourceName || source.sourceName || "",
      SourceDetails: source.SourceDetails || source.sourceDetails || "",
      CreatedBy: source.CreatedBy || source.createdBy || session.userId,
      CreatedOn: source.CreatedOn || source.createdOn || new Date().toISOString(),
      ModifiedBy: session.userId,
      ModifiedOn: new Date().toISOString(),
    });
    setIsEditing(true);
    setActiveTab("1");
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      if (!dateTimeStr) return "N/A";
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  // Don't render if no permission
  if (!hasPermission) {
    return null;
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Source Management" breadcrumbItem="GMS" />
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="wizard clearfix">
                    <div className="steps clearfix">
                      <ul>
                        {!isViewOnly && (
                          <NavItem className={classnames({ current: activeTab === "1" })}>
                            <NavLink
                              className={classnames({ current: activeTab === "1" })}
                              onClick={() => setActiveTab("1")}
                            >
                              <span className="number">1.</span> {isEditing ? "Edit Source" : "Add Source"}
                            </NavLink>
                          </NavItem>
                        )}
                        <NavItem className={classnames({ current: activeTab === "2" })}>
                          <NavLink
                            className={classnames({ active: activeTab === "2" })}
                            onClick={() => setActiveTab("2")}
                          >
                            <span className="number">2.</span> Source List
                          </NavLink>
                        </NavItem>
                      </ul>
                    </div>

                    <div className="content clearfix">
                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                          {!isViewOnly && (
                            <div>
                              <Card>
                                <CardBody>
                                  <h5 className="card-title mb-4">
                                    {isEditing ? "Edit Source" : "Add New Source"}
                                  </h5>
                                  <Form onSubmit={handleSubmit}>
                                    <Row>
                                      <Col md={6}>
                                        <FormGroup className="mb-3">
                                          <Label>Source Name <span className="text-danger">*</span></Label>
                                          <Input
                                            type="text"
                                            name="SourceName"
                                            value={form.SourceName}
                                            onChange={(e) => handleFormChange("SourceName", e.target.value)}
                                            placeholder="Enter source name"
                                            required
                                          />
                                        </FormGroup>
                                      </Col>
                                      <Col md={6}>
                                        <FormGroup className="mb-3">
                                          <Label>Source Details</Label>
                                          <Input
                                            type="text"
                                            name="SourceDetails"
                                            value={form.SourceDetails}
                                            onChange={(e) => handleFormChange("SourceDetails", e.target.value)}
                                            placeholder="Enter source details"
                                          />
                                        </FormGroup>
                                      </Col>
                                    </Row>

                                    <div className="d-flex gap-2">
                                      <Button
                                        type="submit"
                                        color="primary"
                                        disabled={saveLoading}
                                      >
                                        {saveLoading ? (
                                          <>
                                            <ClipLoader size={16} color="#ffffff" className="me-2" />
                                            Saving...
                                          </>
                                        ) : isEditing ? (
                                          "Update Source"
                                        ) : (
                                          "Save Source"
                                        )}
                                      </Button>
                                      {isEditing && (
                                        <Button
                                          type="button"
                                          color="secondary"
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </Button>
                                      )}
                                    </div>
                                  </Form>
                                </CardBody>
                              </Card>
                            </div>
                          )}
                        </TabPane>

                        <TabPane tabId="2">
                          <div>
                            <Card>
                              <CardBody>
                                <h5 className="card-title mb-3">
                                  Source List ({totalCount} records)
                                </h5>
                                
                                {loading ? (
                                  <div className="text-center py-4">
                                    <ClipLoader color="#556ee6" loading={loading} size={50} />
                                  </div>
                                ) : (
                                  <>
                                    <div className="table-responsive">
                                      <Table bordered>
                                        <thead className="table-light">
                                          <tr>
                                            <th>Sr.No</th>
                                            <th>Source Name</th>
                                            <th>Source Details</th>
                                            {hasEditOrHigher && <th>Actions</th>}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sources.length === 0 ? (
                                            <tr>
                                              <td colSpan={hasEditOrHigher ? 4 : 3} className="text-center">
                                                No sources found
                                              </td>
                                            </tr>
                                          ) : (
                                            sources.map((source, index) => {
                                              // Handle both camelCase and PascalCase from API
                                              const sourceId = source.Source_Id || source.source_Id;
                                              const sourceName = source.SourceName || source.sourceName || 'N/A';
                                              const sourceDetails = source.SourceDetails || source.sourceDetails || 'N/A';
                                              
                                              return (
                                                <tr key={sourceId}>
                                                  <td>{(page - 1) * pageSize + index + 1}</td>
                                                  <td>{sourceName}</td>
                                                  <td>{sourceDetails}</td>
                                                  {hasEditOrHigher && (
                                                    <td>
                                                      <Button
                                                        color="info"
                                                        size="sm"
                                                        onClick={() => handleEdit(source)}
                                                        title="Edit Source"
                                                      >
                                                        <SquarePen size={16} className="me-1" />
                                                        Edit
                                                      </Button>
                                                    </td>
                                                  )}
                                                </tr>
                                              );
                                            })
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>

                                    {/* Pagination */}
                                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                      </div>
                                      {totalCount > pageSize && (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                          <Button
                                            color="light"
                                            size="sm"
                                            disabled={page === 1}
                                            onClick={() => handlePageChange(page - 1)}
                                            style={{
                                              border: '1px solid #dee2e6',
                                              borderRadius: '0.375rem',
                                              minWidth: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                              color: page === 1 ? '#6c757d' : '#495057'
                                            }}
                                          >
                                            <i className="mdi mdi-chevron-left"></i>
                                          </Button>
                                          
                                          {[...Array(totalPages)].map((_, index) => {
                                            const pageNum = index + 1;
                                            const isActive = page === pageNum;
                                            
                                            return (
                                              <Button
                                                key={pageNum}
                                                color={isActive ? "primary" : "light"}
                                                size="sm"
                                                onClick={() => handlePageChange(pageNum)}
                                                style={{
                                                  border: '1px solid #dee2e6',
                                                  borderRadius: '0.375rem',
                                                  minWidth: '32px',
                                                  height: '32px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                                  color: isActive ? '#fff' : '#495057',
                                                  fontWeight: isActive ? '600' : '400',
                                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                                }}
                                              >
                                                {pageNum}
                                              </Button>
                                            );
                                          })}
                                          
                                          <Button
                                            color="light"
                                            size="sm"
                                            disabled={page === totalPages}
                                            onClick={() => handlePageChange(page + 1)}
                                            style={{
                                              border: '1px solid #dee2e6',
                                              borderRadius: '0.375rem',
                                              minWidth: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              backgroundColor: page === totalPages ? '#f8f9fa' : '#fff',
                                              color: page === totalPages ? '#6c757d' : '#495057'
                                            }}
                                          >
                                            <i className="mdi mdi-chevron-right"></i>
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </CardBody>
                            </Card>
                          </div>
                        </TabPane>
                      </TabContent>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

